import { Component, EventEmitter, Input, AfterViewInit, Output, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Color, DeviceInfoFull, SealPosition, SealSize, SignaturePadConfig, Stroke } from './models/signature-pad';
import { AuthLoginRequest } from '../../../api';
import { BiometrySignatureRequest } from '../../../api/model/biometrySignatureRequest';
import { BiometryV5Service } from './services/biometryV5.service';
import { AuthV5Service } from './services/AuthV5.service';
import * as moment from 'moment';
import * as FileSaver from 'file-saver';
import * as CipherModule from './cipher/cipher'
import { SignatureCanvasComponent } from './signature-canvas/signature-canvas.component';
import { UAParser } from 'ua-parser-js';
import xml2js from 'xml2js';
import { PointGroup } from 'signature_pad';

@Component({
  selector: 'app-ivpad',
  templateUrl: './iv-pad.component.html',
  styleUrls: ['./iv-pad.component.scss'],
  providers: [AuthV5Service, BiometryV5Service]
})
export class IvPadComponent implements AfterViewInit {
  /**
   * Cadena con la imagen de la firma codificada en base64.
   */
  @Input() public signature: string;

  /**
   * Objeto con la configuración del componente.
   */
  @Input() public config: SignaturePadConfig = {
    showButtons: true,
    showColorPicker: false,
    buttons: {
      save: { active: true, label: 'Firmar' },
      clear: { active: true, label: 'Limpiar' },
      cancel: { active: true, label: 'Cancelar' }
    },
    startOpened: true,
    isModal: false,
    penColor: 'rgb(0, 74, 193)',
    canvasWidth: 300,
    canvasHeight: 300,
    minWidth: 1,
    backgroundColor: 'rgba(0, 0, 0, 0)',
    biometry: true,
    pdf: {
      signtext: false
    },
    autoDownload: true,
    colors: {}
  };

  /**
   * Boolean del estado inicial del garabato (visible/no visible).
   */
  @Input() public startOpened: boolean = this.config.startOpened;
  /**
   * Indicates if the pad is visible
   */
  @Input() public opened: boolean = this.startOpened;
  /**
   * Sets if the pad is being used in a dialog
   */
  @Input() public isModal: boolean = this.config.isModal;
  /**
   * Sets if the buttons (Save/Clear) should be visible
   */
  @Input() public showButtons: boolean = this.config.showButtons;
  /**
   * Sets if the component should sign the generates document through API after emitting onRequestReady event.
   * If set to false, the document will not be signed, but the prepared request object will still be emitter through onRequest Ready event.
   */
  @Input() public signingEnabled: boolean = true;
  /**
   * Array con los puntos dibujados.
   */
  public data: PointGroup[] = [];
  /**
   * Boolean del estado de la firma biométrica (activada/desactivada).
   */
  public biometry: boolean = false;

  @ViewChild('signatureCanvas') public signatureCanvas: SignatureCanvasComponent;
  /**
   * Evento disparado al empezar a firmar.
   */
  @Output() public start: EventEmitter<boolean> = new EventEmitter<boolean>();
  /**
   * Evento disparado durante la firma.
   */
  @Output() public update: EventEmitter<unknown> = new EventEmitter<unknown>();
  /**
   * Evento disparado .
   */
  @Output() public onError: EventEmitter<unknown> = new EventEmitter<unknown>();
  /**
   * Evento disparado al terminar de firmar.
   */
  @Output() public end: EventEmitter<boolean> = new EventEmitter<boolean>();
  /**
   * Evento disparado al guardar la imagen de la firma.
   */
  @Output() public save: EventEmitter<unknown> = new EventEmitter<unknown>();
  /**
   * Evento disparado al cancelar del guardado de la imagen de la firma.
   */
  @Output() public cancel: EventEmitter<unknown> = new EventEmitter<unknown>();
  /**
   * Evento disparado al inicializar el componente de firma.
   */
  @Output() public loaded: EventEmitter<boolean> = new EventEmitter<boolean>();
  /**
   * Event emitted when the login has been succesful.
   */
  @Output() public loggedIn: EventEmitter<string> = new EventEmitter<string>();
  /**
   * Emitted before signing the document calling the API. Contains the request object that should be passed to API.
   */
  @Output() public onRequestReady: EventEmitter<BiometrySignatureRequest> = new EventEmitter<BiometrySignatureRequest>();
  /**
   * Objeto con la información sobre el dispositivo.
   */
  public deviceInfo: DeviceInfoFull = {
    Device: [],
    DeviceId: [],
    Company: [],
    Computer: [],
    User: [],
    Type: []
  };
  /**
   * Array de las propiedades del objeto/variable 'deviceInfo'
   */
  public dKeys: string[] = Object.keys(this.deviceInfo);
  /**
   * Boolean de la visibilidad del spinner loader.
   * Visible (true) / Oculto (false).
   */
  public loading: boolean = false;
  /**
   * Cadena con la descripcion del error ocurrido (null si no lo hay).
   */
  public error: string = null;
  /**
   * Objeto para identificarse con IvSign y obtener token
   */
  public auth: AuthLoginRequest = {
    orgaid: '',
    login: '',
    pass: '',
    module: 'api5',
    authmethod: 'pass'
  };
  /**
   * Indicates if the login form is shown
   */
  public showLogin: boolean = false;
  /**
   * Objeto con la configuración del plugin Signature Pad.
   */
  public signaturePadOptions = {
    minWidth: this.config.minWidth,
    canvasWidth: this.config.canvasWidth,
    canvasHeight: this.config.canvasHeight,
    backgroundColor: this.config.backgroundColor,
    penColor: this.config.penColor
  };
  /**
   * FormGroup for the login form.
   */
  public FormLogin: FormGroup = this.fb.group({
    orgaid: ['', Validators.required],
    login: ['', Validators.required],
    pass: ['', Validators.required]
  });
  /**
   * Signed PDF file..
   */
  public file: string = null;
  /**
   * Indicates if the errors ocurred should be visualized by the component
   */
  public showErrors: boolean = true;
  /**
   * Indicates if the loading indicator should be visualized by the component
   */
  public showLoading: boolean = true;
  /**
   * Indicates if the login should be enabled.
   */
  public loginEnabled: boolean = false;
  /**
   * Object containing the certificate information
   */
  private cert = {
    id: '',
    pin: ''
  };
  /**
   * Cadena con el archivo pdf para firmar codificado en base64.
   */
  public pdf: string = ''; // null;
  /**
   * Cadena con la clave pública del certificado usada para cifrar la llave AES.
   */
  private publicKey: string = ''; // null;
  /**
   * Modulo para cifrar el XML.
   */
  private cipherModule: typeof CipherModule = CipherModule;
  /**
   * Boolean que indica si el componente ha sido inicializado.
   */
  private initialized: boolean = false;
  /**
   * Cadena con el token de IvSign para firmar el pdf.
   */
  private token: string = ''; // null;
  /**
   * Objeto con la posición de la marca gráfica del sellado (x empieza de izquierda, y de abajo).
   */
  private sealPosition: SealPosition = { x: 0, y: 0, page: '1' };
  /**
   * Tamaño de la marca gráfica del sellado (w: width, h: height)
   */
  private sealSize: SealSize = { w: 84, h: 84 };
  /**
   * Booleano que indica el estado de modo offline.
   */
  private offline: boolean = false;
  /**
   * Booleano que indica el estado de modo offline.
   */
  public isCanvasReady: boolean = false;

  constructor(
    private authService: AuthV5Service,
    private biometryService: BiometryV5Service,
    private fb: FormBuilder
  ) { }

  /**
   * Ciclo de vida que se llamada después de que Angular haya incializado la vista de un componente.
   */
  public ngAfterViewInit(): void {
    this.initialized = true;
    this.loaded.emit(true);
    this.parseDeviceData();
    this.checkSignature();
  }

  /**
   * Funcion que cambia el color de la firma.
   * @param {Color} color Color de la imagen (rgba)
   */
  public changeColorPicker(color: Color): void {
    this.signatureCanvas.setOptions(
      'penColor',
      'rgb(' + color.r + ', ' + color.g + ', ' + color.b + ')'
    );
  }

  /**
   * Setea credenciales de IvSign
   * @param {string} orgaid ID de la organización
   * @param {string} login Nombre de usuario
   * @param {string} pass Contraseña de usuario
   */
  public setIvSignCredentials(orgaid: string, login: string, pass: string): void {
    if (orgaid) {
      this.FormLogin.get('orgaid').patchValue(orgaid);
    }
    if (login) {
      this.FormLogin.get('login').patchValue(login);
    }
    if (pass) {
      this.FormLogin.get('pass').patchValue(pass);
    }
  }

  /**
   * Funcion que recupera la cadena con la imagen de la firma codificada en base64.
   * @param {string} type Tipo de la imagen (por defecto PNG, otros: "image/jpeg", "image/svg+xml")
   * @returns Cadena con la imagen de la firma codificada en base64.
   */
  public getSignature(type?: string): string {
    return this.signatureCanvas.toDataURL(type);
  }

  /**
   * Funcion que recupera el array con los puntos de la firma.
   * @returns {PointGroup[]} Array de grupos de puntos que representan la  imagen de la firma.
   */
  public getSignatureData(): PointGroup[] {
    return this.signatureCanvas.toData();
  }

  /**
   * Funcion que guarda la cadena de la imagen de la firma, codificada en base64, en la variable y emite el evento 'save' con la misma imagen.
   * @param {boolean} sign Boolean que indica si debe iniciarse la firma biometrica contra IvSign, del pdf, al guardar la imagen.
   * @param {string} type Cadena que indica el tipo de la imagen (por defecto JPEG, otros: 'image/png', 'image/svg+xml').
   */
  public drawSave(sign: boolean = false, type: string = 'image/jpeg'): void {
    this.signature = this.signatureCanvas.toDataURL(type);
    if (!this.startOpened) {
      this.opened = false;
    }
    this.save.emit({
      type: 'image',
      content: this.signature.replace('data:' + type + ';base64,', '')
    });
    if (sign) {
      this.signPDF();
    }
  }

  /**
   * Retrieves the signed file
   */
  public getFile(): string {
    return this.file;
  }

  /**
   * Funcion que limpia el Array de puntos que componen la firma (garabato).
   */
  public clearSign(): void {
    this.signatureCanvas.clear();
  }

  /**
   * Funcion que dispara el evento de cancelacion de la firma.
   */
  public onCancel(): void {
    this.cancel.emit();
    this.togglePad(false);
  }

  /**
   * Funcion que cambia los parámetros de configuración de la firma.
   * @param {Config} newConfig Objeto que contiene los parámetros de la nueva configuración para la firma.
   */
  public changeConfig(newConfig: SignaturePadConfig): void {
    const keys: string[] = [];
    if (newConfig !== this.config) {
      for (const key of Object.keys(newConfig)) {
        const value = newConfig[key];
        if (value !== undefined && this.config[key] !== value) {
          this.config[key] = value;
          keys.push(key);
        }
      }
      if (keys.length) {
        this.applyConfig(keys);
      }
    }
  }

  /**
   * Funcion que aplica los cambios de configuración de la firma.
   * @param {string[]} keys Array que contiene los distintos parámetros de la configuración de la firma.
   */
  public applyConfig(keys: string[]): void {
    for (const key of keys) {
      const arr = ['penColor', 'canvasWidth', 'canvasHeight', 'minWidth', 'backgroundColor'];
      if (arr.indexOf(key) !== -1) {
        this.setConfig({ name: key, value: this.config[key] });
      } else if (key === 'language') {
        this.opened = !this.opened;
      }
    }
  }

  /**
   * Funcion que genera un XML con los datos de la firma.
   * @returns {string} Cadena con el contenido del XML como string.
   */
  public dataToXML(): string {
    const data = this.getSignatureData();
    const time = data[0].points[0].time;

    const json = {
      Signature: {
        HashDoc: [''],
        DeviceInfo: [{
          Device: this.deviceInfo.Device || [],
          DeviceId: this.deviceInfo.DeviceId || [],
          Company: this.deviceInfo.Company || [],
          Computer: this.deviceInfo.Computer || [],
          User: this.deviceInfo.User || []
        }],
        SignatureInfo: [{
          Orientacion: ['TopOrigin'],
          Time: [moment(time).format()],
          XExtent: [this.config.canvasWidth.toString()],
          YExtent: [this.config.canvasHeight.toString()],
          Resolution: [{
            Unit: ['px'],
            Horizontal: ['1'],
            Vertical: ['1']
          }],
          Unit: ['Point'],
          TimeUnit: ['Ticks'],
          PressureScale: ['1024'],
          PressureRFormat: ['integer'],
          biometricVersion: ['0'],
          biometricFormat: ['0'],
          signatureProfile: [] as string[],
          signatureType: ['Default'],
          id: [] as string[],
          account: [] as string[],
          biometricApplication: ['CWIvSignBiometric'],
          biometricDevice: this.deviceInfo.Type // Mobile/Tablet/Desktop
        }],
        SignatureData: [{
          GraphicData: [{
            Stroke: [] as Stroke[]
          }],
        }],
        SignatureImage: [this.getSignature('image/png').replace('data:image/png;base64,', '')],
        SignatureFormat: ['PNG']
      }
    };

    const j = json.Signature.SignatureData[0].GraphicData[0].Stroke;
    for (const d of data) {
      j.push({
        Point: []
      });
      for (const a of d.points) {
        j[j.length - 1].Point.push({
          Point: [{
            X: [a.x.toString()],
            Y: [a.y.toString()]
          }],
          Pressure: [a['force'] !== undefined || a['force'] != null ? a['force'].toString() : '1'],
          Time: [(a.time - time).toString()]
        });

      }
    }

    const builder = new xml2js.Builder();
    return builder.buildObject(json);
  }

  /**
   * Funcion para descargar el XML de la firma.
   */
  public downloadXML(): void {
    const blob = new Blob([this.dataToXML()], { type: 'text/xml;charset=utf-8' });
    const filename = 'sig.xml';
    FileSaver.saveAs(blob, filename);
  }

  /**
   * Funcion que para abrir/cerrar el garabato de firma.
   * @param {boolean} display Boolean que indica si hay que mostrar el garabato (true/false).
   */
  public togglePad(display: boolean): void {
    if (this.startOpened) {
      return;
    }
    this.opened = display;
  }

  /**
   * Funcion para comprobar si está activada la firma biométrica.
   * @returns Boolean del estado de activación de la firma biométrica (true/false).
   */
  public isBiometry(): boolean {
    return this.biometry;
  }

  /**
   * Funcion que establece la información del dispositivo en la variable 'deviceInfo'.
   * @param {Partial<DeviceInfoFull>} data Objeto con las propiedades del dispositivo.
   */
  public setDeviceInfo(data: Partial<DeviceInfoFull>): void {
    const keys = ['Device', 'DeviceId', 'Company', 'Computer', 'User', 'Type'];
    const temp = {
      Device: [data.manufacturer + ' ' + data.model],
      DeviceId: [data.id],
      Company: [data.manufacturer],
      Computer: [data.id],
      User: [data.user],
      Type: [data.type.charAt(0).toUpperCase() + data.type.slice(1)]
    };
    for (const key of keys) {
      if (temp[key]) {
        this.deviceInfo[key] = temp[key];
      }
    }
  }

  /**
   * Funcion para parsear la información sobre el dispositivo (usando UA).
   */
  public parseDeviceData(): void {
    if (!navigator.userAgent) {
      return;
    }
    const parser = new UAParser();
    const result = parser.getResult();

    const data = {
      manufacturer: result.device.vendor || '',
      model: result.device.model,
      type: result.device.type,
      id: null as string,
      browser: result.browser.name,
      browserVersion: result.browser.version,
      OS: result.os.name,
      OSVersion: result.os.version,
      user: null as string,
    };

    if (result.os.name === 'Windows') {
      data.manufacturer = 'Microsoft';
      data.model = '';
      data.type = this.isTouch() ? 'Tablet' : 'Desktop';
    } else if (result.os.name === 'Mac OS') {
      data.manufacturer = 'Apple';
      data.model = '';
      data.type = 'Desktop';
    } else if (!result.device.vendor && !result.device.model && !result.device.type) {
      data.manufacturer = result.os.name;
      data.model = '';
      data.type = this.isTouch() ? 'Tablet' : 'Desktop';
    }

    this.setDeviceInfo(data);
  }

  /**
   *
   */
  public checkBiometrySupport(): boolean {
    return false;
  }

  /**
   * Funcion para establecer el pdf a firmar.
   * @param {string} pdf Cadena con PDF para firmar codificado en base64.
   */
  public setPdf(pdf: string): void {
    this.pdf = pdf;
  }

  /**
   * Funcion para establecer la clave pública que se usa para el cifrado de la llave AES.
   * @param {string} key Cadena con la clave pública del certificado.
   */
  public setPublicKey(key: string): void {
    this.publicKey = key;
  }

  /**
   * Funcion para establecer las propiedades del certificado que se va a usar.
   * @param {string} id Cadena con la id del certificado.
   * @param {string} pin Cadena con el pin del certificado.
   */
  public setCert(id: string, pin: string): void {
    this.cert.id = id;
    this.cert.pin = pin;
  }

  /**
   * Funcion para cifrar un string.
   * @param {string} text Cadena con el XML que hay que cifrar.
   * @returns {string} Cadena con los datos biometricos cifrados.
   */
  public cipher(text: string): string {
    return this.cipherModule.generate(text, this.publicKey);
  }

  /**
   * Funcion para cerrar el modal de error.
   */
  public dismissError(): void {
    this.error = null;
  }

  /**
   * Sets the signature position in the PDF file
   * @param {number} x Vertical coordinate (from bottom border)
   * @param {number} y Horizontal coordinate (from the left)
   * @param {string} page Page or pages interval where the signature should be shown.
   */
  public setSealPosition(x: number, y: number, page: string): void {
    this.sealPosition.x = x;
    this.sealPosition.y = y;
    this.sealPosition.page = page;
  }

  /**
   *  Sets the signature size
   * @param {number} w Width of the signature container
   * @param {number} h Height of the signature container
   */
  public setSealSize(w: number, h: number): void {
    this.sealSize.w = w;
    this.sealSize.h = h;
  }

  /**
   * Checks if the login screen should be shown.
   */
  public loginCheck(): boolean {
    if ((!this.token && this.biometry && !this.offline) !== this.showLogin) {
      this.showLogin = !this.token && this.biometry && !this.offline;
    }
    return this.showLogin;
  }

  /**
   * Emits error (internal or from the API) ocurred
   * @param {unknown} error String or object to be emitted
   */
  public emitError(error: unknown): void {
    this.loading = false;
    this.onError.emit(error);
  }

  /**
   * Disables visualization of errors.
   */
  public disableErrors(): void {
    this.showErrors = false;
  }

  /**
   * Disables visualizing of the loading indicator
   */
  public disableLoading(): void {
    this.showLoading = false;
  }

  /**
   * Enables the login form
   */
  public enableLogin(): void {
    this.loginEnabled = true;
  }

  /**
   * Deactivates the signing of the document calling API
   */
  public disableSigning(): void {
    this.signingEnabled = false;
  }

  /**
   * Enables the signing of the document calling API
   */
  public enableSigning(): void {
    this.signingEnabled = true;
  }

  /**
   * Funcion para realizaz la firma biométrica del PDF contra IvSign.
   * @param {boolean} download Boolean para establecer la descarga automática del pdf firmado.
   * @returns {string} Puede devolver una cadena con el XML cifrado.
   */
  public signPDF(download: boolean = this.config.autoDownload, multiple: boolean = false): string {
    this.loading = true;
    if (!this.pdf) {
      this.error = 'Falta PDF.';
      this.emitError(this.error);
      return;
    }
    if (this.checkSignature()) {
      this.error = 'Falta la firma.';
      this.emitError(this.error);
      return;
    }

    if (this.offline) {
      this.loading = false;
      return this.cipher(this.dataToXML());
    }

    const request: BiometrySignatureRequest = {
      document: this.pdf,
      profile: 'enhanced',
      extensions: 't,timestamp,revinfo,biometry',
      hashalgorithm: '',
      parameters: {
        pdfparameters: {
          signvisible: true,
          signbackgroundconfig: {
            signback: this.getSignature('image/png').replace('data:image/png;base64,', ''),
            signbackautostretch: true,
            transparencymask: {
              red: 255,
              green: 255,
              blue: 255,
              tolerance: 10
            }
          },
          widgetprops: {
            autopos: false,
            offsetx: this.sealPosition.x,
            offsety: this.sealPosition.y,
            autosize: false,
            width: this.sealSize.w,
            height: this.sealSize.h,
            rotate: 0,
            showonpages: this.sealPosition.page,
            widgetpageoffset: 0,
            hidetext: !this.config.pdf.signtext,
            sizeheader: 5.5,
            sizedatetime: 4,
            sizetitlesection: 5,
            sizetextsection: 4.5
          }
        },
        biometry: this.isBiometry() ? {
          data: this.cipher(this.dataToXML()),
          cer: this.publicKey.replace('-----BEGIN CERTIFICATE-----', '').replace('-----END CERTIFICATE-----', '')
        } : null
      }
    };

    this.onRequestReady.emit(request);
    this.file = null;
    if (!this.signingEnabled) {
      return;
    }

  }

  /**
   * Funcion para establecer el token para la firma.
   * @param {string} token Cadena con el token de autenticación de IvSign.
   */
  public setToken(token: string): void {
    this.token = token;
    this.loginCheck();
  }

  /**
 * Recibe el token a través de la autenticación contra IvSign
 */
  public getIvSignToken(): void {
    this.loading = true;
    const val = this.FormLogin.value;
    this.auth.orgaid = val.orgaid;
    this.auth.login = val.login;
    this.auth.pass = val.pass;
    this.authService.authAuthLogin(this.auth).subscribe(
      (response) => {
        if (!response.token) {
          if (response.error) {
            this.error = response.error.message;
            this.emitError(response);
          }
          return false;
        }
        this.setToken(response.token);
        this.loggedIn.emit(response.token);
        this.loading = false;
      },
      (error) => {
        this.error = JSON.stringify(error);
        this.emitError(error);
      }
    );
  }

  /**
   * Funcion para comprobar si el garabato tiene contenido.
   * @returns {boolean} Boolean que indica si existe una firma (false) o no (true).
   */
  public checkSignature(): boolean {
    this.isCanvasReady = this.initialized || !!this.signatureCanvas;
    return this.signatureCanvas.isEmpty();
  }

  /**
   * Funcion para dispara el evento 'start' al empezar a firmar.
   */
  public drawStart(): void {
    this.start.emit(true);
  }

  /**
   * Funcion para disparar el evento 'end' al terminar de firmar.
   */
  public drawComplete(event: boolean): void {
    this.end.emit(event);
    this.data = this.signatureCanvas.toData();
  }

  /**
   * Funcion para establecer el valor del estado de la firma biométrica.
   * @param {boolean} value Boolean del estado del garabato (visible/no visible).
   */
  public setBiometry(value: boolean): void {
    this.biometry = value;
  }

  /**
   * Funcion para establecer el valor del modo offline.
   * @param {boolean} value Boolean del estado del modo offline.
   */
  public setOffline(value: boolean): void {
    this.offline = value;
  }

  /**
   * Sets the color of the elements like buttons
   * @param {string} primary Primary color
   * @param {string} text Text color (black/white)
   */
  public setColor(primary: string, text: string): void {
    this.config.colors.primary = primary;
    this.config.colors.text = text;
  }

  /**
   * Sets the base URL of the API that the component should call when authenticating ans signing
   * @param {string} url URL address
   */
  public setApi(url: string): void {
    this.biometryService.basePath = url;
    this.authService.basePath = url;
  }

  /**
   * Funcion para establecer la configuración del componente de Signature Pad.
   * @param configuration Objeto con las propiedades de la configuracion del componente Signature Pad.
   */
  private setConfig(configuration: { [key: string]: any }): void {
    this.signatureCanvas.setOptions(configuration.name, configuration.value);
  }

  /**
   * Funcion para comprobar si existe Touch API.
   * @returns {boolean} Boolean que indica si hay soporte de Touch API.
   */
  private isTouch(): boolean {
    const prefixes = ' -webkit- -moz- -o- -ms- '.split(' ');
    const mq = (query: string) => {
      return window.matchMedia(query).matches;
    };

    if ('ontouchstart' in window) {
      return true;
    }

    const query2 = ['(', prefixes.join('touch-enabled),('), 'heartz', ')'].join('');
    return mq(query2);
  }

}
